import { isDesktop } from '@/utils/deviceUtils';

const METADATA_KEY = 'opfs_cache_metadata';
const MAX_STORAGE_BYTES = !isDesktop() && process.env.NODE_ENV === 'production'
    ? 2 * 1024 * 1024 * 1024
    : 10 * 1024 * 1024 * 1024;
const WRITE_CHUNK_SIZE = 16 * 1024 * 1024;

export class OPFSQuotaExceededError extends Error {
    constructor(message: string = 'OPFS storage quota exceeded') {
        super(message);
        this.name = 'QuotaExceededError';
    }
}

interface OPFSMetadata {
    size: number;
    createdAt: number;
    lastAccessedAt: number;
    originalName?: string;
}

interface OPFSMetadataStore {
    [key: string]: OPFSMetadata;
}

export class OPFSService {
    private opfsRoot: FileSystemDirectoryHandle | null = null;

    private async getOPFSRoot(): Promise<FileSystemDirectoryHandle> {
        if (!this.opfsRoot) {
            this.opfsRoot = await navigator.storage.getDirectory();
        }
        return this.opfsRoot;
    }

    private getMetadata(): OPFSMetadataStore {
        try {
            const data = localStorage.getItem(METADATA_KEY);
            return data ? JSON.parse(data) : {};
        } catch (error) {
            console.error('Failed to read metadata:', error);
            return {};
        }
    }

    private saveMetadata(metadata: OPFSMetadataStore): void {
        try {
            localStorage.setItem(METADATA_KEY, JSON.stringify(metadata));
        } catch (error) {
            console.error('Failed to save metadata:', error);
        }
    }

    private willExceedMaxLimit(bytes: number): boolean {
        const trackedUsage = Object.values(this.getMetadata()).reduce((sum, m) => sum + m.size, 0);
        return trackedUsage + bytes > MAX_STORAGE_BYTES;
    }

    private isQuotaError(error: unknown): boolean {
        if (error instanceof DOMException) {
            return error.name === 'QuotaExceededError';
        }
        if (error instanceof Error) {
            return error.message.toLowerCase().includes('quota');
        }
        return false;
    }

    private async reconcileStorage(): Promise<number> {
        const metadata = this.getMetadata();
        const opfsRoot = await this.getOPFSRoot();
        const foundFiles = new Set<string>();
        let totalSize = 0;

        for await (const [name, handle] of opfsRoot.entries()) {
            if (handle.kind === 'file') {
                foundFiles.add(name);
                try {
                    const file = await (handle as FileSystemFileHandle).getFile();
                    totalSize += file.size;

                    // Update or create metadata for the file
                    if (!metadata[name]) {
                        console.log(`Found untracked file: ${name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
                        metadata[name] = {
                            size: file.size,
                            createdAt: file.lastModified,
                            lastAccessedAt: file.lastModified,
                            originalName: file.name
                        };
                    } else {
                        // Ensure size is accurate
                        metadata[name].size = file.size;
                    }
                } catch (e) {
                    console.warn(`Error reading file ${name}:`, e);
                }
            }
        }

        for (const key in metadata) {
            if (!foundFiles.has(key)) {
                console.log(`Removing stale metadata: ${key}`);
                delete metadata[key];
            }
        }

        this.saveMetadata(metadata);
        return totalSize;
    }

    private async pruneOldFiles(bytesNeeded: number = -1, protectedKey?: string): Promise<void> {
        const metadata = this.getMetadata();
        const entries = Object.entries(metadata);

        let freed = 0;

        for (const [key, meta] of entries) {
            if (meta.size === 0 && key !== protectedKey) {
                await this.deleteFile(key);
            }
        }

        const remainingEntries = Object.entries(this.getMetadata())
            .filter(([key, meta]) => meta.size > 0 && key !== protectedKey)
            .sort(([, a], [, b]) => a.lastAccessedAt - b.lastAccessedAt);

        for (const [key, meta] of remainingEntries) {
            if (freed >= bytesNeeded && bytesNeeded !== -1) {
                break;
            }

            console.log(`Pruning old file: ${key} (${(meta.size / 1024 / 1024).toFixed(2)} MB)`);
            await this.deleteFile(key);
            freed += meta.size;
        }

        console.log(`Freed ${(freed / 1024 / 1024).toFixed(2)} MB`);
    }

    private async reconcileAndPrune(bytesNeeded: number, protectedKey?: string): Promise<void> {
        try {
            const currentUsage = await this.reconcileStorage();

            console.log(`Storage check: ${(currentUsage / 1024 / 1024).toFixed(2)} MB / ${(MAX_STORAGE_BYTES / 1024 / 1024 / 1024).toFixed(0)} GB used`);

            if (currentUsage + bytesNeeded > MAX_STORAGE_BYTES) {
                const bytesToFree = currentUsage + bytesNeeded - MAX_STORAGE_BYTES;
                console.log(`Pruning ${(bytesToFree / 1024 / 1024).toFixed(2)} MB to free space`);
                await this.pruneOldFiles(bytesToFree, protectedKey);
            }
        } catch (error) {
            console.warn('Storage reconciliation failed:', error);
        }
    }

    public async getFileHandle(key: string, opts: Record<any, any> = {}): Promise<FileSystemFileHandle> {
        const opfsRoot = await this.getOPFSRoot();
        return await opfsRoot.getFileHandle(key, opts);
    }

    public async finalizeFile(key: string, originalName?: string): Promise<File> {
        const handle = await this.getFileHandle(key);

        const file = await handle.getFile();
        const metadata = this.getMetadata();
        console.log(`Finalized file: ${key} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);

        metadata[key] = {
            size: file.size,
            createdAt: Date.now(),
            lastAccessedAt: Date.now(),
            originalName: originalName || file.name
        };

        this.saveMetadata(metadata);

        if (originalName && originalName !== file.name) {
            return new File([file], originalName, {
                type: file.type,
                lastModified: file.lastModified
            });
        }

        return file;
    }

    private async writeFileToOPFS(key: string, file: File, originalName?: string): Promise<void> {
        const handle = await this.getFileHandle(key, { create: true });
        const writable = await handle.createWritable();

        try {
            // WebKit reads a Blob passed to write() fully into memory before writing it,
            // which gets the tab killed on iOS for large videos
            for (let offset = 0; offset < file.size; offset += WRITE_CHUNK_SIZE) {
                await writable.write(file.slice(offset, offset + WRITE_CHUNK_SIZE));
            }
            await writable.close();
        } catch (error) {
            try {
                await writable.close();
            } catch {}
            throw error;
        }

        const metadata = this.getMetadata();
        metadata[key] = {
            size: file.size,
            createdAt: Date.now(),
            lastAccessedAt: Date.now(),
            originalName: originalName || file.name
        };

        this.saveMetadata(metadata);
    }

    public async saveFile(key: string, file: File, originalName?: string): Promise<void> {
        if (this.willExceedMaxLimit(file.size)) {
            await this.reconcileAndPrune(file.size);
        }

        try {
            await this.writeFileToOPFS(key, file, originalName);
        } catch (error) {
            if (this.isQuotaError(error)) {
                await this.reconcileAndPrune(file.size);
                try {
                    await this.writeFileToOPFS(key, file, originalName);
                } catch (retryError) {
                    throw new Error(`Failed to save file to OPFS: ${retryError instanceof Error ? retryError.message : 'Unknown error'}`);
                }
            } else {
                throw error;
            }
        }

        console.log(`Saved file to OPFS: ${key} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    }

    public async getFile(key: string): Promise<File | null> {
        const metadata = this.getMetadata();
        const meta = metadata[key];

        if (!meta) {
            return null;
        }

        try {
            const handle = await this.getFileHandle(key);
            const file = await handle.getFile();

            meta.lastAccessedAt = Date.now();
            this.saveMetadata(metadata);

            console.log(`Retrieved cached file: ${key}`);

            if (meta.originalName && meta.originalName !== file.name) {
                return new File([file], meta.originalName, {
                    type: file.type,
                    lastModified: file.lastModified
                });
            }

            return file;
        } catch (error) {
            console.warn(`Failed to retrieve file: ${key}`, error);
            delete metadata[key];
            this.saveMetadata(metadata);
            return null;
        }
    }

    public async clearAll(): Promise<void> {
        const opfsRoot = await this.getOPFSRoot();
        for await (const [name] of opfsRoot.entries()) {
            await opfsRoot.removeEntry(name).catch(() => {});
        }
        localStorage.removeItem(METADATA_KEY);
    }

    public async deleteFile(key: string): Promise<void> {
        const metadata = this.getMetadata();

        try {
            const opfsRoot = await this.getOPFSRoot();
            await opfsRoot.removeEntry(key);
        } catch (error) {
            console.warn(`Failed to delete OPFS file: ${key}`, error);
        }

        delete metadata[key];
        this.saveMetadata(metadata);
    }

    /**
     * Proactively checks for available space by attempting to allocate a temporary file.
     * If quota is exceeded, attempts to prune old files and retries.
     * @param bytes Number of bytes to reserve
     * @param protectedKey Optional file key to protect from deletion during pruning
     */
    public async allocateSpace(bytes: number, protectedKey?: string): Promise<void> {
        if (this.willExceedMaxLimit(bytes)) {
            await this.reconcileAndPrune(bytes, protectedKey);
        }

        const tempKey = `_allocation_check_${Date.now()}_${Math.random().toString(36).slice(2)}`;

        try {
            await this.performAllocation(tempKey, bytes);
        } catch (error) {
            if (this.isQuotaError(error)) {
                console.log(`[OPFSService] Quota exceeded during check for ${(bytes / 1024 / 1024).toFixed(2)} MB. Pruning...`);
                await this.pruneOldFiles(bytes, protectedKey);
                try {
                    await this.performAllocation(tempKey, bytes);
                } catch (retryError) {
                    throw new OPFSQuotaExceededError('Insufficient storage space after pruning old files.');
                }
            } else {
                throw error;
            }
        } finally {
            // Clean up the temporary check file
            try {
                const opfsRoot = await this.getOPFSRoot();
                await opfsRoot.removeEntry(tempKey).catch(() => {});
            } catch (e) {}
        }
    }

    private async performAllocation(key: string, bytes: number): Promise<void> {
        const handle = await this.getFileHandle(key, { create: true });
        const writable = await handle.createWritable();
        try {
            await writable.truncate(bytes);
        } finally {
            await writable.close().catch(() => {});
        }
    }
}

const opfsService = new OPFSService();
export default opfsService;
